import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {Button, Column, Row, Spinner, Text} from "react-vcomponents";
import {ScrollView} from "react-vscrollview";
import {RunInAction} from "web-vcore";
import {ShowMessageBox} from "react-vmessagebox";
import {MediaComp} from "../../../../Engine/LiveSession/Components/MediaComp.js";
import {GetLiveLocalSession_Reactive} from "../../../../Engine/LiveSessionManagement.js";
import {GetAudioFilesWithSubpath} from "../../../../Store/main/general/CacheState.js";
import {AudioFileEntry} from "../../../../Utils/Bridge/Bridge_Native/MediaTypes.js";
import {InAndroid} from "../../../../Utils/Bridge/Bridge_Native.js";
import {mockAudioFiles} from "../../../../Utils/Media/MockAudioFiles.js";
import {SpinnerForPercent} from "../../../../Utils/ReactComponents/SpinnerForPercent.js";

function FormatTime(seconds: number): string {
	const s = isFinite(seconds) ? seconds : 0;
	const mins = Math.floor(s / 60);
	const secs = Math.floor(s % 60);
	return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export const MediaPanel = observer_mgl(()=>{
	// Local seeking state — while the user drags the slider we show a local position
	// so the thumb doesn't jump; we only Seek() on mouse/touch release.
	const [isSeeking, setIsSeeking] = React.useState(false);
	const [seekPos, setSeekPos] = React.useState(0);
	const controllerRef = React.useRef<any>(null);

	const liveLocalSession = GetLiveLocalSession_Reactive();
	const mediaComp = liveLocalSession?.Comp(MediaComp);
	// should this quick-return be kept?
	if (mediaComp == null) return <Column style={{flex: 1, padding: 5}}>Session must be active to use this panel.</Column>

	const uiState = mediaComp?.uiState;
	const displayPos = isSeeking ? seekPos : (uiState?.currentPosition ?? 0);
	const fileName = uiState?.selectedFile?.filePath.split("/").pop() ?? "(no file selected)";

	const openFilePicker = ()=>{
		controllerRef.current = ShowMessageBox({
			title: "Select Audio File",
			okOnEnterKey: false,
			message: ()=>(
				<MediaFilePicker
					mediaComp={mediaComp}
					onClose={()=>controllerRef.current?.Close()}
				/>
			),
		});
	};

	return (
		<Column style={{flex: 1, padding: 5, minHeight: 0}}>
			{/* File label + select button */}
			<Row center>
				<Text style={{flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
					title={uiState?.selectedFile?.filePath}>
					{fileName}
				</Text>
				<Button ml={5} mdIcon="folder-open-outline" title="Select audio file" onClick={openFilePicker}/>
			</Row>

			{/* Transport controls + speed */}
			<Row center mt={5}>
				<Button mdIcon="skip-backward" title="Jump back 10 s"
					enabled={mediaComp != null}
					onClick={()=>mediaComp?.JumpBackward()}/>
				<Button ml={5}
					mdIcon={uiState?.isPlaying ? "pause" : "play"}
					title={uiState?.isPlaying ? "Pause" : "Play"}
					enabled={mediaComp != null && uiState?.selectedFile != null}
					onClick={()=>mediaComp?.TogglePlaying()}/>
				<Button ml={5} mdIcon="skip-forward" title="Jump forward 10 s"
					enabled={mediaComp != null}
					onClick={()=>mediaComp?.JumpForward()}/>

				<Text ml={15}>Speed:</Text>
				<Spinner ml={5} min={0.1} max={16} step={mediaComp?.c.speedStep ?? 0.1} instant
					value={uiState?.playbackSpeed ?? 1}
					onChange={val=>{
						mediaComp?.SetPlaybackSpeed(val);
					}}/>
				<Text ml={3}>×</Text>

				<Text ml={15}>Vol:</Text>
				<SpinnerForPercent ml={5} min={0} max={100} instant
					value={uiState?.volumeMultiplier ?? 1}
					onChange={val=>mediaComp?.SetVolumeMultiplier(val)}/>
			</Row>

			{/* Progress slider */}
			<Row center mt={5}>
				<Text style={{minWidth: 42, textAlign: "right"}}>{FormatTime(displayPos)}</Text>
				<input
					type="range"
					style={{flex: 1, margin: "0 8px", cursor: "pointer"}}
					min={0}
					max={(uiState?.duration ?? 0) > 0 ? uiState?.duration : 1}
					step={0.1}
					value={displayPos}
					onMouseDown={()=>{ setIsSeeking(true); setSeekPos(uiState?.currentPosition ?? 0); }}
					onChange={e=>setSeekPos(parseFloat(e.target.value))}
					onMouseUp={e=>{ mediaComp?.Seek(parseFloat((e.target as HTMLInputElement).value)); setIsSeeking(false); }}
					onTouchStart={()=>{ setIsSeeking(true); setSeekPos(uiState?.currentPosition ?? 0); }}
					onTouchEnd={e=>{ mediaComp?.Seek(parseFloat((e.target as HTMLInputElement).value)); setIsSeeking(false); }}
				/>
				<Text style={{minWidth: 42}}>{FormatTime(uiState?.duration ?? 0)}</Text>
			</Row>

			{/* Desync-caught button; note that trigger-set/hotkey is the primary/expected signal mechanism */}
			<Row mt={5} style={{justifyContent: "flex-end"}}>
				<Button style={{fontSize: 11}}
					text={uiState?.desync_isActive ? "⚡ Mark desync caught" : "Mark desync caught"}
					title="Signal noticing a desync between audio-panning and breathing (note: button has lightning-emoji when desync active)"
					onClick={()=>mediaComp?.MarkDesyncCaught()}/>
			</Row>
		</Column>
	);
});

// ---- File picker dialog ----

const MediaFilePicker = observer_mgl((props: {mediaComp: MediaComp|null|undefined, onClose: ()=>void})=>{
	const {mediaComp, onClose} = props;
	const uiState = mediaComp?.uiState;
	const config = mediaComp?.c;
	const pools = config?.mediaPools ?? [];

	const [pickerPoolIndex, setPickerPoolIndex] = React.useState(uiState?.currentPoolIndex ?? 0);
	const selectedPool = pools[pickerPoolIndex];
	const audioFiles: AudioFileEntry[] = selectedPool ? GetAudioFilesWithSubpath(selectedPool.path) : [];
	if (audioFiles.length === 0 && !InAndroid(0)) {
		audioFiles.push(...mockAudioFiles);
	}

	return (
		<Column style={{width: window.innerWidth - 50, height: window.innerHeight * 0.85}}>
			{/* Pool selector row */}
			<Row style={{flexWrap: "wrap", gap: 5, padding: "8px 5px", borderBottom: "1px solid rgba(255,255,255,.2)"}}>
				{pools.length === 0
					? <Text>(no media pools configured)</Text>
					: pools.map((pool, i)=>(
						<Button key={i} p="3px 8px"
							text={pool.name || `Pool ${i + 1}`}
							style={pickerPoolIndex === i ? {backgroundColor: "rgba(100,200,100,.5)"} : undefined}
							onClick={()=>setPickerPoolIndex(i)}/>
					))
				}
			</Row>

			{/* File list */}
			{selectedPool == null
				? <Text p={5}>(select a pool above)</Text>
				: audioFiles.length === 0
					? <Text p={5}>No audio files found matching path: &quot;{selectedPool.path}&quot;</Text>
					: (
						<ScrollView style={{flex: 1}}>
							<Column style={{padding: 5, gap: 3}}>
								{audioFiles.map((file, i)=>{
									const name = file.filePath.split("/").pop() ?? file.filePath;
									const isSelected = file.filePath === uiState?.selectedFile?.filePath;
									return (
										<Button key={i}
											style={{
												justifyContent: "flex-start",
												...(isSelected ? {backgroundColor: "rgba(100,200,100,.3)"} : {}),
											}}
											text={name}
											title={file.filePath}
											onClick={()=>{
												mediaComp?.SelectFile(file);
												RunInAction("MediaFilePicker.select", ()=>{
													if (mediaComp) mediaComp.uiState.currentPoolIndex = pickerPoolIndex;
												});
												onClose();
											}}/>
									);
								})}
							</Column>
						</ScrollView>
					)
			}
		</Column>
	);
});